import React, { useState, useEffect } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useTranslation } from 'react-i18next';
import './LedgerView.css';

/**
 * Ledger View — pick a ledger, see its transactions with running balance.
 *
 * Accepts an optional `?ledger_id=N` query string OR a navigation event
 * with detail.ledgerId so users can click through from the Ledgers page.
 */
export default function LedgerView({ initialLedgerId }) {
  const { t } = useTranslation();
  const [ledgerId, setLedgerId] = useState(initialLedgerId || null);
  const [from, setFrom]         = useState('');
  const [to, setTo]             = useState(today());

  // Initial range: current month
  useEffect(() => {
    if (!from) {
      const d = new Date();
      setFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10));
    }
  }, [from]);

  // Listen for navigate-with-ledger events from the Chart of Accounts page
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.page === 'LedgerView' && e.detail?.ledgerId) {
        setLedgerId(e.detail.ledgerId);
      }
    };
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, []);

  const { data: allLedgers } = useApi(() => api.ledgers.list(), [], { rows: [] });

  const { data, loading, error, reload } = useApi(
    () => ledgerId && from && to
      ? api.ledgers.transactions(ledgerId, from, to)
      : Promise.resolve(null),
    [ledgerId, from, to]
  );

  return (
    <div className="lv-page">
      <div className="page-header">
        <h2 className="page-title">{t('ledgerView.title')}</h2>
        {data?.ledger && (
          <button className="btn btn-sm" onClick={reload}>🔄 {t('common.refresh')}</button>
        )}
      </div>

      {error && <ErrBanner msg={error} />}
      
      {data?.ledger && Number(data.ledger.is_active) === 0 && (
        <div style={{
          background: '#fff5f5', color: '#c53030', padding: '10px 15px', 
          borderRadius: 8, border: '1px solid #feb2b2', marginBottom: 15,
          fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10
        }}>
          ⚠️ {t('ledgerView.deactivated')}
        </div>
      )}

      <div className="lv-filters section-card">
        <div className="form-group lv-ledger-pick">
          <label>{t('ledgerView.ledger')}</label>
          <select value={ledgerId || ''} onChange={e => setLedgerId(e.target.value ? parseInt(e.target.value) : null)}>
            <option value="">{t('ledgerView.selectLedger')}</option>
            {(allLedgers.rows || []).map(l => (
              <option key={l.ledger_id} value={l.ledger_id}>
                [{l.ledger_type}] {l.name}
                {Number(l.is_active) === 0 ? ' (' + t('common.inactive') + ')' : ''}
                {Number(l.closing_balance) !== 0
                  ? ` — ₹${fmtNum(l.closing_balance)}`
                  : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>{t('common.from')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>{t('common.to')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {!ledgerId ? (
        <div className="section-card" style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
          {t('ledgerView.pickLedger')}
        </div>
      ) : loading ? <Spinner /> : !data ? null : (
        <>
          {/* Header */}
          <div className="lv-header section-card">
            <div className="lv-title">
              <h3>{data.ledger.name}</h3>
              <div className="lv-subtitle">
                <span className={`lv-type-pill lv-type-${(data.ledger.ledger_type || '').toLowerCase()}`}>
                  {data.ledger.ledger_type}
                </span>
                {data.ledger.sub_group && <span className="lv-sub-group">{data.ledger.sub_group}</span>}
                <span style={{ color: 'var(--text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                  {data.ledger.code}
                </span>
              </div>
            </div>
            <div className="lv-balances">
              <Bal label={t('common.opening')} value={data.opening} side={data.opening_side} />
              <Bal label={t('ledgerView.totalDr')} value={data.total_debit} side="Dr" tone="green" />
              <Bal label={t('ledgerView.totalCr')} value={data.total_credit} side="Cr" tone="orange" />
              <Bal label={t('common.closing')} value={data.closing} side={data.closing_side} highlight />
            </div>
          </div>

          {/* Ledger book */}
          <div className="section-card" style={{ overflowX: 'auto' }}>
            <div className="section-header">
              {data.ledger.name} — {fmtDate(from)} {t('ledgerView.to')} {fmtDate(to)} ({data.rows.length} {data.rows.length !== 1 ? t('ledgerView.transactions') : t('ledgerView.transaction')})
            </div>
            <table className="lv-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>{t('common.date')}</th>
                  <th style={{ textAlign: 'left' }}>{t('common.particulars')}</th>
                  <th style={{ textAlign: 'left' }}>{t('common.voucher')}</th>
                  <th style={{ textAlign: 'right' }}>{t('common.debit')}</th>
                  <th style={{ textAlign: 'right' }}>{t('common.credit')}</th>
                  <th style={{ textAlign: 'right' }}>{t('common.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Opening row */}
                <tr className="lv-opening-row">
                  <td style={{ textAlign: 'left' }}>{fmtDate(from)}</td>
                  <td colSpan="2" style={{ textAlign: 'left', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                    {t('ledgerView.openingBalance')}
                  </td>
                  <td></td>
                  <td></td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                    {fmtNum(Math.abs(data.opening))}
                    <span className="lv-side-tag">{data.opening_side}</span>
                  </td>
                </tr>

                {data.rows.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                    {t('ledgerView.noTransactions')}
                  </td></tr>
                ) : data.rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'left' }}>{fmtDate(r.entry_date)}</td>
                    <td style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 500 }}>{r.particulars || '—'}</div>
                      {r.narration && r.narration !== r.particulars && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {r.narration}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'left', fontFamily: 'monospace', fontSize: 11.5 }}>
                      <span className={`lv-ref-pill lv-ref-${r.reference_type}`}>{r.reference_type}</span>
                      <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>{r.reference_no}</div>
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--secondary)', fontWeight: 600 }}>
                      {r.debit > 0 ? fmtNum(r.debit) : ''}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace', color: 'var(--warning)', fontWeight: 600 }}>
                      {r.credit > 0 ? fmtNum(r.credit) : ''}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                      {fmtNum(Math.abs(r.running_balance))}
                      <span className="lv-side-tag">{r.balance_side}</span>
                    </td>
                  </tr>
                ))}

                {/* Total row */}
                <tr className="lv-total-row">
                  <td colSpan="3" style={{ textAlign: 'right', fontWeight: 700 }}>
                    {t('ledgerView.periodTotals')}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: 'var(--secondary)' }}>
                    {fmtNum(data.total_debit)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: 'var(--warning)' }}>
                    {fmtNum(data.total_credit)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, fontSize: 14 }}>
                    {fmtNum(Math.abs(data.closing))}
                    <span className="lv-side-tag lv-side-final">{data.closing_side}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


function Bal({ label, value, side, tone, highlight }) {
  return (
    <div className={`lv-bal ${tone ? 'lv-bal-' + tone : ''} ${highlight ? 'lv-bal-highlight' : ''}`}>
      <div className="lv-bal-label">{label}</div>
      <div className="lv-bal-value">
        ₹ {fmtNum(Math.abs(Number(value || 0)))}
        {side && <span className="lv-bal-side">{side}</span>}
      </div>
    </div>
  );
}
